import {DatePipe} from '@angular/common';
import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {finalize} from 'rxjs';
import {FactorialProjectService} from '../../services/factorial-project.service';
import {FactorialProject} from '../../models/FactorialProject';
import {ToastService} from '../../services/toast.service';
import {QrChallengeService} from '../../services/qr-challenge.service';
import {QrChallenge} from '../../models/QrChallenge';
import {TotemDeviceService} from '../../services/totem-device.service';
import {QrTotemConfigurationService} from '../../services/qr-totem-configuration.service';
import {QrTotemConfiguration} from '../../models/QrTotemConfiguration';

@Component({
  selector: 'app-qr-generator',
  templateUrl: './ts-export.component.html',
  imports: [
    ReactiveFormsModule,
    DatePipe
  ],
  standalone: true
})
export class QrGeneratorComponent implements OnInit, OnDestroy {

  private fb = inject(UntypedFormBuilder);
  private projectService = inject(FactorialProjectService);
  private qrChallengeService = inject(QrChallengeService);
  private totemConfigurationService = inject(QrTotemConfigurationService);
  private totemDevice = inject(TotemDeviceService);
  private sanitizer = inject(DomSanitizer);
  public form!: UntypedFormGroup;
  public projects = signal<FactorialProject[]>([]);
  public loadingProjects = signal(false);
  public loadingTotemConfiguration = signal(false);
  public generatingQr = signal(false);
  public openingConfiguredQr = signal(false);
  public activeChallenge = signal<QrChallenge | null>(null);
  public qrViewerOpen = signal(false);
  public qrSvg = signal<SafeHtml | null>(null);
  public secondsRemaining = signal(0);
  public totemConfiguration = signal<QrTotemConfiguration | null>(null);
  public canChangeProject = computed(() => {
    const configuration = this.totemConfiguration();

    if (!configuration) {
      return true;
    }

    return configuration.can_change_project === true;
  });
  public projectLockedForCurrentUser = computed(() => !!this.totemConfiguration() && !this.canChangeProject());
  public previousQrAvailable = computed(() => !!this.activeChallenge() && !this.qrViewerOpen());
  public toast = inject(ToastService);
  private deviceToken = '';
  private countdownIntervalId?: number;
  private refreshTimeoutId?: number;

  ngOnInit(): void {
    this.deviceToken = this.totemDevice.getToken();
    this.form = this.fb.group({
      project_id: ['', [Validators.required]],
      password: [''],
    });

    this.loadTotemConfiguration();
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  loadProjects(): void {
    if (!this.loadingProjects()) {
      this.loadingProjects.set(true);
      this.projectService.index()
        .pipe(finalize(() => this.loadingProjects.set(false)))
        .subscribe({
          next: (response) => {
            this.projects.set(response.data ?? []);
          },
          error: () => {
            this.projects.set([]);
            this.toast.error('Errore durante il recupero dei progetti');
          }
        });
    }
  }

  loadTotemConfiguration(): void {
    if (this.loadingTotemConfiguration()) {
      return;
    }

    this.loadingTotemConfiguration.set(true);
    this.totemConfigurationService.lookup(this.deviceToken)
      .pipe(finalize(() => this.loadingTotemConfiguration.set(false)))
      .subscribe({
        next: (response) => {
          const configuration = response.data ?? null;

          this.applyTotemConfiguration(configuration);

          if (configuration?.project_id) {
            this.openingConfiguredQr.set(true);
            this.requestChallenge(configuration.project_id);
            return;
          }

          this.loadProjects();
        },
        error: () => {
          this.toast.error('Errore durante il recupero della configurazione del tablet');
          this.loadProjects();
        }
      });
  }

  onGenerateQr(): void {
    const projectId = this.projectIdForChallenge();

    if (!projectId || (this.canChangeProject() && this.form.invalid)) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.changingProjectRequiresPassword() && !this.changePassword()) {
      this.form.get('password')?.markAsTouched();
      return;
    }

    this.requestChallenge(
      projectId,
      this.changingProjectRequiresPassword() ? this.changePassword() : undefined
    );
  }

  closeQrViewer(): void {
    this.qrViewerOpen.set(false);
    this.secondsRemaining.set(0);
    this.clearTimers();

    if (this.canChangeProject() && this.projects().length === 0) {
      this.loadProjects();
    }
  }

  refreshQrCode(): void {
    this.requestChallenge(this.activeChallenge()?.project_id ?? this.projectIdForChallenge());
  }

  openPreviousQrViewer(): void {
    const projectId = this.activeChallenge()?.project_id;

    if (!projectId) {
      return;
    }

    this.requestChallenge(projectId);
  }

  onProjectSelectionChange(): void {
    if (!this.changingProjectRequiresPassword()) {
      this.form.patchValue({password: ''});
    }
  }

  changingProjectRequiresPassword(): boolean {
    const configuration = this.totemConfiguration();
    const selectedProjectId = this.selectedProjectId();

    return !!configuration
      && this.canChangeProject()
      && configuration.requires_password_to_change_project === true
      && selectedProjectId !== ''
      && String(configuration.project_id ?? '') !== selectedProjectId;
  }

  passwordMissingForProjectChange(): boolean {
    return this.changingProjectRequiresPassword() && !this.changePassword();
  }

  openBrowserFullscreen(container: HTMLElement): void {
    const fullscreenRequest = container.requestFullscreen?.();

    fullscreenRequest?.catch(() => {
      this.toast.show('Impossibile attivare il full screen del browser', 'warning');
    });
  }

  private requestChallenge(projectId: string, password?: string): void {
    if (!projectId || this.generatingQr()) {
      return;
    }

    this.generatingQr.set(true);
    this.qrChallengeService.create(projectId, this.deviceToken, password)
      .pipe(finalize(() => {
        this.generatingQr.set(false);
        this.openingConfiguredQr.set(false);
        this.form.patchValue({password: ''});
      }))
      .subscribe({
        next: (response) => {
          const challenge = response.data;

          this.applyTotemConfiguration(challenge.totem_configuration ?? null);
          this.activeChallenge.set(challenge);
          this.qrViewerOpen.set(true);
          this.qrSvg.set(this.sanitizer.bypassSecurityTrustHtml(challenge.svg ?? ''));
          this.form.patchValue({project_id: challenge.project_id ?? projectId});
          this.syncChallengeTimers();
        },
        error: (error) => {
          this.toast.error(error?.error?.message ?? 'Errore durante la generazione del QR');

          if (this.canChangeProject() && this.projects().length === 0) {
            this.loadProjects();
          }
        }
      });
  }

  private applyTotemConfiguration(configuration: QrTotemConfiguration | null): void {
    this.totemConfiguration.set(configuration);

    if (configuration?.project_id) {
      this.form.patchValue({project_id: configuration.project_id});
    }
  }

  private syncChallengeTimers(): void {
    this.clearTimers();
    this.updateCountdown();

    const expiresAt = this.challengeExpiryTime();
    if (!expiresAt) {
      return;
    }

    this.countdownIntervalId = window.setInterval(() => {
      this.updateCountdown();
    }, 1000);

    const refreshInMs = Math.max(1000, expiresAt - Date.now() - 1000);
    this.refreshTimeoutId = window.setTimeout(() => {
      this.refreshQrCode();
    }, refreshInMs);
  }

  private updateCountdown(): void {
    const expiresAt = this.challengeExpiryTime();

    if (!expiresAt) {
      this.secondsRemaining.set(0);
      return;
    }

    const remainingMs = Math.max(0, expiresAt - Date.now());
    this.secondsRemaining.set(Math.ceil(remainingMs / 1000));
  }

  private clearTimers(): void {
    if (this.countdownIntervalId) {
      window.clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = undefined;
    }

    if (this.refreshTimeoutId) {
      window.clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = undefined;
    }
  }

  private challengeExpiryTime(): number | null {
    const expiresAt = this.activeChallenge()?.expires_at;

    if (!expiresAt) {
      return null;
    }

    const time = new Date(expiresAt).getTime();

    return Number.isNaN(time) ? null : time;
  }

  private selectedProjectId(): string {
    return String(this.form.get('project_id')?.value ?? '');
  }

  private changePassword(): string {
    return String(this.form.get('password')?.value ?? '');
  }

  private projectIdForChallenge(): string {
    const configuration = this.totemConfiguration();

    if (configuration && !this.canChangeProject()) {
      return String(configuration.project_id ?? '');
    }

    return this.selectedProjectId();
  }
}
